import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { FeedbackButtons } from '~/components/shared/feedback-buttons'
import {
    PassLikelihoodBadge,
    VerdictBadge,
} from '~/components/assessment/assessment-badges'
import { scaleLabel } from '~/components/assessment/labels'
import { cn } from '~/lib/utils'
import type {
    AssessmentReport,
    AssessmentResponse,
    ChecklistItem,
    ItemScore,
    SurveyAnswer,
} from '~/api/types'
import {
    SCORING_CRITERIA_NOTE,
    compactAnswerText,
    itemCategoryPath,
    passLikelihoodBasis,
    priorityScores,
    scoreToneClass,
    scoreTotal,
    verdictCount,
} from './utils'
import { CriteriaHoverTab } from './criteria-hover-tab'

export function ReportSummaryTab({
    assessment,
    report,
    scores,
    checklist,
    answers,
    onReport,
    reporting,
}: {
    assessment: AssessmentResponse
    report: AssessmentReport | null
    scores: ItemScore[]
    checklist: ChecklistItem[] | null
    answers: SurveyAnswer[]
    onReport(): void
    reporting: boolean
}) {
    const total = scoreTotal(scores)
    const poorCount = verdictCount(scores, '부적합')
    const insufficientCount = verdictCount(scores, '미흡')
    const passCount = verdictCount(scores, '적합')
    const titleById = new Map<string, ChecklistItem>()
    for (const item of checklist ?? []) titleById.set(item.id, item)
    const priorities = priorityScores(scores).filter(
        (score) => score.verdict !== '적합'
    )
    const topPriorities = priorities.slice(0, 5)
    const reportStats = report?.content.stats
    const reportGaps = report?.content.gaps ?? []
    const totalScore = reportStats?.totalScore ?? total.score
    const maxScore = reportStats?.maxScore ?? total.maxScore

    return (
        <div className='grid gap-4 pb-6 print:pb-0'>
            <Card className='print:border-0 print:shadow-none'>
                <CardHeader>
                    <div className='flex flex-wrap items-start justify-between gap-4'>
                        <div>
                            <p className='text-primary text-sm font-semibold'>
                                HACCP 평가 보고서
                            </p>
                            <CardTitle className='mt-1 text-xl'>
                                {assessment.title}
                            </CardTitle>
                            <p className='text-muted-foreground mt-2 text-sm'>
                                {assessment.classification ?? '분류 미정'} ·{' '}
                                {scaleLabel(assessment.scale)}
                                {assessment.industry
                                    ? ` · ${assessment.industry}`
                                    : ''}
                            </p>
                        </div>
                        {assessment.passLikelihood ? (
                            <PassLikelihoodBadge
                                likelihood={assessment.passLikelihood}
                            />
                        ) : null}
                    </div>
                </CardHeader>
                <CardContent className='space-y-4'>
                    <div className='grid gap-3 sm:grid-cols-4'>
                        <div className='border-border rounded-md border p-4'>
                            <p className='text-muted-foreground text-xs'>
                                총점
                            </p>
                            <p
                                className={cn(
                                    'mt-2 text-2xl font-bold',
                                    scoreToneClass(
                                        totalScore ?? 0,
                                        maxScore ?? 0
                                    )
                                )}>
                                {totalScore ?? 0}/{maxScore ?? 0}
                            </p>
                        </div>
                        <div className='border-border rounded-md border p-4'>
                            <p className='text-muted-foreground text-xs'>
                                적합
                            </p>
                            <p className='text-success mt-2 text-2xl font-bold'>
                                {passCount}
                            </p>
                        </div>
                        <div className='border-border rounded-md border p-4'>
                            <p className='text-muted-foreground text-xs'>
                                미흡
                            </p>
                            <p className='text-warning mt-2 text-2xl font-bold'>
                                {insufficientCount}
                            </p>
                        </div>
                        <div className='border-border rounded-md border p-4'>
                            <p className='text-muted-foreground text-xs'>
                                부적합
                            </p>
                            <p className='text-destructive mt-2 text-2xl font-bold'>
                                {poorCount}
                            </p>
                        </div>
                    </div>

                    {/* 채점 기준 명시(HACCP 기준) — 왜 이 점수·합격가능성인지 설명. */}
                    <div className='border-border bg-muted/30 rounded-md border p-3'>
                        <p className='text-foreground text-xs font-semibold'>
                            채점 기준 안내
                        </p>
                        {assessment.passLikelihood ? (
                            <p className='text-muted-foreground mt-1.5 text-xs leading-relaxed'>
                                {passLikelihoodBasis(
                                    totalScore ?? 0,
                                    maxScore ?? 0,
                                    assessment.passLikelihood
                                )}
                            </p>
                        ) : null}
                        <p className='text-muted-foreground mt-1.5 text-xs leading-relaxed'>
                            {SCORING_CRITERIA_NOTE}
                        </p>
                    </div>
                </CardContent>
            </Card>

            <div className='grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]'>
                <Card>
                    <CardHeader>
                        <CardTitle>전체 총평</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-3'>
                        {report?.summary || report?.content.narrative ? (
                            <>
                                {report.summary ? (
                                    <p className='text-foreground text-sm leading-relaxed whitespace-pre-wrap'>
                                        {report.summary}
                                    </p>
                                ) : null}
                                {report.content.narrative ? (
                                    <p className='text-foreground text-sm leading-relaxed whitespace-pre-wrap'>
                                        {report.content.narrative}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <p className='text-foreground text-sm leading-relaxed'>
                                    현재 평가는 {maxScore ?? 0}점 만점 중{' '}
                                    {totalScore ?? 0}점입니다. 적합 항목보다
                                    보완이 필요한 항목이 먼저 눈에 들어오도록
                                    정리했으며, 아래 우선 조치 항목부터 확인하는
                                    흐름이 적절합니다.
                                </p>
                                <p className='text-muted-foreground text-sm leading-relaxed'>
                                    AI 총평이 필요한 경우 상단의 보고서
                                    생성하기를 누르면 항목별 결과를 바탕으로 더
                                    구체적인 총평을 생성합니다.
                                </p>
                            </>
                        )}
                        <div className='border-border border-t pt-3 print:hidden'>
                            <FeedbackButtons
                                service='certification'
                                targetType='report'
                                targetId={assessment.id}
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>현재 상태</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <dl className='grid gap-3 sm:grid-cols-2'>
                            <div className='border-border rounded-md border p-3'>
                                <dt className='text-muted-foreground text-xs'>
                                    평가 범위
                                </dt>
                                <dd className='text-foreground mt-1 text-sm font-medium'>
                                    {assessment.classification ?? '분류 미정'} ·{' '}
                                    {scaleLabel(assessment.scale)}
                                </dd>
                            </div>
                            <div className='border-border rounded-md border p-3'>
                                <dt className='text-muted-foreground text-xs'>
                                    보완 필요
                                </dt>
                                <dd className='text-foreground mt-1 text-sm font-medium'>
                                    {poorCount + insufficientCount}개 항목
                                </dd>
                            </div>
                            <div className='border-border rounded-md border p-3 sm:col-span-2'>
                                <dt className='text-muted-foreground text-xs'>
                                    업종
                                </dt>
                                <dd className='text-foreground mt-1 text-sm font-medium'>
                                    {assessment.industry || '-'}
                                </dd>
                            </div>
                        </dl>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>앞으로 해야 할 일</CardTitle>
                </CardHeader>
                <CardContent>
                    {topPriorities.length === 0 ? (
                        <p className='text-muted-foreground text-sm'>
                            우선 보완이 필요한 항목이 없습니다. 현재 관리 상태를
                            유지하고 기록을 계속 축적하세요.
                        </p>
                    ) : (
                        <>
                            {/* 권고 조치는 모든 카드가 사실상 동일하므로 상단에 1회만 통합한다.
                                항목별 실제 기준은 각 카드의 '체크리스트 기준' 호버 탭에서 본다. */}
                            <div className='bg-accent border-border mb-4 rounded-md border p-4'>
                                <p className='text-primary text-sm font-semibold'>
                                    권고 조치
                                </p>
                                <p className='text-foreground mt-2 text-sm leading-relaxed'>
                                    아래 항목들의 기준(기준·관리·현장)에 맞게 절차,
                                    기록, 현장 상태를 보완한 뒤 사진·점검표·관리기록으로
                                    확인 근거를 남기세요. 공통 절차는 “보완 →
                                    기록·사진 근거 확보 → 재점검”이며, 항목별 세부
                                    기준은 각 카드의 <b>체크리스트 기준</b>에 마우스를
                                    올리면 확인할 수 있습니다.
                                </p>
                            </div>
                            <ol className='grid gap-3'>
                            {topPriorities.map((score, index) => {
                                const item = titleById.get(score.itemId)
                                return (
                                    <li
                                        key={score.itemId}
                                        className='border-border rounded-md border p-4'>
                                        <div className='flex items-start justify-between gap-3'>
                                            <div className='min-w-0 flex-1'>
                                                <p className='text-primary text-sm font-semibold'>
                                                    {index + 1}. 우선 조치
                                                </p>
                                                <h3 className='text-foreground mt-1 text-base leading-relaxed font-semibold'>
                                                    {item?.title ??
                                                        score.itemId}
                                                </h3>
                                                <p className='text-muted-foreground mt-1 text-sm'>
                                                    {item
                                                        ? itemCategoryPath(item)
                                                        : '체크리스트 상세 없음'}
                                                </p>
                                            </div>
                                            <div className='flex shrink-0 items-center gap-2'>
                                                {score.verdict ? (
                                                    <VerdictBadge
                                                        verdict={score.verdict}
                                                    />
                                                ) : null}
                                                <CriteriaHoverTab item={item} />
                                            </div>
                                        </div>
                                        <div className='bg-muted/30 border-border mt-4 rounded-md border p-3'>
                                            <p className='text-muted-foreground text-xs'>
                                                현재 답변
                                            </p>
                                            <p className='text-foreground mt-2 text-sm leading-relaxed whitespace-pre-wrap'>
                                                {compactAnswerText(
                                                    item,
                                                    answers
                                                )}
                                            </p>
                                        </div>
                                    </li>
                                )
                            })}
                            </ol>
                        </>
                    )}
                </CardContent>
            </Card>

            {reportGaps.length > 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>보고서 보완 항목</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ul className='list-disc space-y-2 pl-5'>
                            {reportGaps.map((gap, index) => (
                                <li
                                    key={`${index}-${gap}`}
                                    className='text-foreground text-sm leading-relaxed'>
                                    {gap}
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            ) : null}

            {!report ? (
                <Card>
                    <CardHeader>
                        <CardTitle>AI 총평 생성</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className='text-muted-foreground text-sm'>
                            상단의 보고서 생성하기를 누르면 현재 채점 결과를
                            바탕으로 총평과 보완 항목을 저장합니다.
                        </p>
                        <Button
                            className='mt-4'
                            onClick={onReport}
                            disabled={reporting}>
                            {reporting ? '생성 중…' : '보고서 생성하기'}
                        </Button>
                    </CardContent>
                </Card>
            ) : null}
        </div>
    )
}
